import { isErrorLike, serializeError } from "@-/com.npmjs.serialize-error";

const MAX_MESSAGE_LENGTH = 2000;
const MAX_ERROR_LENGTH = 6000;

function truncate(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit)}… [+${text.length - limit} chars]` : text;
}

function stringifyValue(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint" ||
    value === null ||
    value === undefined
  ) {
    return String(value);
  }
  if (typeof value === "symbol" || typeof value === "function") {
    return value.toString();
  }
  try {
    const json = JSON.stringify(value);
    return json === undefined ? Object.prototype.toString.call(value) : json;
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/** Renders a serialized error (name, message, stack) as one bounded string. */
export function formatDiagnosticsError(error: unknown): string | null {
  if (error === undefined || error === null) {
    return null;
  }
  if (error instanceof Error || isErrorLike(error)) {
    const serialized = serializeError(error);
    const name = typeof serialized.name === "string" ? serialized.name : "Error";
    const message = typeof serialized.message === "string" ? serialized.message : "";
    const stack = typeof serialized.stack === "string" ? serialized.stack : "";
    const head = `${name}: ${message}`;
    return truncate(
      stack.startsWith(head) || stack.startsWith(name) ? stack : `${head}\n${stack}`,
      MAX_ERROR_LENGTH,
    );
  }
  if (typeof error === "object") {
    // serialize-error output from LogLayer: a plain object with the error's own properties.
    const record: Record<string, unknown> = { ...error };
    const name = typeof record.name === "string" ? record.name : "Error";
    const message = typeof record.message === "string" ? record.message : stringifyValue(error);
    const stack = typeof record.stack === "string" ? record.stack : "";
    return truncate(stack.length > 0 ? stack : `${name}: ${message}`, MAX_ERROR_LENGTH);
  }
  return truncate(stringifyValue(error), MAX_ERROR_LENGTH);
}

/**
 * Joins console-style arguments into one bounded message, pulling out the first `Error` so it
 * can be stored as a separate, stack-bearing field.
 */
export function formatLogArguments(args: readonly unknown[]): {
  error: string | null;
  message: string;
} {
  let error: string | null = null;
  const parts: string[] = [];
  for (const arg of args) {
    if (error === null && (arg instanceof Error || isErrorLike(arg))) {
      error = formatDiagnosticsError(arg);
      continue;
    }
    parts.push(stringifyValue(arg));
  }
  return { error, message: truncate(parts.join(" "), MAX_MESSAGE_LENGTH) };
}
